import { BaseAPI } from "@/api/baseAPI";
import apiClient from "@/api/apiClient";
import { extractApiData } from "@/utils/apiResponseHandler";
import type { GeneralReserveItem } from "@/types";

class GeneralReserveItemAPI extends BaseAPI<GeneralReserveItem> {
  constructor() {
    super("generalReserveItem");
  }

  /**
   * Fetch all general reserve items for a specific organisation
   * @param {number} organisationId - The ID of the organisation
   * @returns {Promise<Array>} - A promise resolving to an array of general reserve items
   */
  async fetchByOrganisationId(organisationId: number): Promise<GeneralReserveItem[]> {
    return await apiClient
      .get(`${this.model}/organisation/${organisationId}`)
      .then(extractApiData);
  }

  /**
   * Create a new general reserve item for a specific organisation
   * @param {number} organisationId - The ID of the organisation
   * @param {Object} data - The general reserve item data
   * @returns {Promise<Object>} - A promise resolving to the created general reserve item
   */
  async createForOrganisation(
    organisationId: number,
    data: Partial<GeneralReserveItem>
  ): Promise<GeneralReserveItem> {
    return await apiClient
      .post(`${this.model}/organisation/${organisationId}`, data)
      .then(extractApiData);
  }

  /**
   * Delete all general reserve items for a specific organisation
   * @param {number} organisationId - The ID of the organisation
   * @returns {Promise<void>} - A promise resolving when the items are deleted
   */
  async deleteByOrganisationId(organisationId: number): Promise<void> {
    return await apiClient
      .delete(`${this.model}/organisation/${organisationId}`)
      .then(extractApiData);
  }

  /**
   * Get the sum of all general reserve items for a specific organisation
   * @param {number} organisationId - The ID of the organisation
   * @returns {Promise<Object>} - A promise resolving to an object with total and total_formatted
   */
  async getSum(
    organisationId: number
  ): Promise<{ total: number; total_formatted: string }> {
    return await apiClient
      .get(`${this.model}/organisation/${organisationId}/sum`)
      .then(extractApiData);
  }
}

export default new GeneralReserveItemAPI();
